/**
 * Gestionnaire centralisé des tokens d'authentification
 * Résout les problèmes de tokens invalides et de gestion incohérente
 */

import { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';

// Types de tokens supportés
export const TOKEN_TYPES = {
  CLIENT: 'access_token',
  RESTAURANT: 'restaurant_token', 
  ADMIN: 'admin_access_token'
};

// Configuration des tokens
const TOKEN_CONFIG = {
  [TOKEN_TYPES.CLIENT]: {
    storageKey: 'access_token',
    refreshKey: 'refresh_token',
    userKey: 'user',
    loginEndpoint: '/users/login',
    meEndpoint: '/users/me'
  },
  [TOKEN_TYPES.RESTAURANT]: {
    storageKey: 'restaurant_token',
    refreshKey: 'restaurant_refresh_token',
    userKey: 'restaurant_data',
    loginEndpoint: '/restaurants/login',
    meEndpoint: '/restaurants/me'
  },
  [TOKEN_TYPES.ADMIN]: {
    storageKey: 'admin_access_token',
    refreshKey: 'admin_refresh_token',
    userKey: 'admin_data',
    loginEndpoint: '/admin/login',
    meEndpoint: '/admin/me'
  }
};

/**
 * Vérifie si un token est valide (non expiré)
 */
export const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const isExpired = payload.exp * 1000 < Date.now();
    return !isExpired;
  } catch (error) {
    console.error('Erreur lors de la vérification du token:', error);
    return false;
  }
};

/**
 * Récupère un token depuis le localStorage
 */
export const getToken = (tokenType = TOKEN_TYPES.CLIENT) => {
  const config = TOKEN_CONFIG[tokenType];
  if (!config) {
    throw new Error(`Type de token non supporté: ${tokenType}`);
  }
  
  return localStorage.getItem(config.storageKey);
};

/**
 * Stocke un token dans le localStorage
 */
export const setToken = (token, tokenType = TOKEN_TYPES.CLIENT) => {
  const config = TOKEN_CONFIG[tokenType];
  if (!config) {
    throw new Error(`Type de token non supporté: ${tokenType}`);
  }
  
  localStorage.setItem(config.storageKey, token);
};

/**
 * Supprime un token du localStorage
 */
export const removeToken = (tokenType = TOKEN_TYPES.CLIENT) => {
  const config = TOKEN_CONFIG[tokenType];
  if (!config) {
    throw new Error(`Type de token non supporté: ${tokenType}`);
  }
  
  localStorage.removeItem(config.storageKey);
  localStorage.removeItem(config.refreshKey);
  localStorage.removeItem(config.userKey);
};

/**
 * Supprime tous les tokens
 */
export const clearAllTokens = () => {
  Object.values(TOKEN_TYPES).forEach(tokenType => {
    removeToken(tokenType);
  });
};

/**
 * Vérifie l'authentification pour un type de token donné
 */
export const checkAuth = async (tokenType = TOKEN_TYPES.CLIENT) => {
  const token = getToken(tokenType);
  
  if (!token) {
    return { isAuthenticated: false, error: 'Aucun token trouvé' };
  }
  
  if (!isTokenValid(token)) {
    removeToken(tokenType);
    return { isAuthenticated: false, error: 'Token expiré' };
  }
  
  try {
    const config = TOKEN_CONFIG[tokenType];
    const response = await fetch(`${API_BASE_URL}${config.meEndpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const userData = await response.json();
      localStorage.setItem(config.userKey, JSON.stringify(userData));
      return { isAuthenticated: true, user: userData };
    } else {
      removeToken(tokenType);
      return { isAuthenticated: false, error: 'Token invalide' };
    }
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'authentification:', error);
    return { isAuthenticated: false, error: 'Erreur de connexion' };
  }
};

/**
 * Effectue une connexion avec gestion d'erreurs améliorée
 */
export const performLogin = async (credentials, tokenType = TOKEN_TYPES.CLIENT) => {
  const config = TOKEN_CONFIG[tokenType];
  if (!config) {
    throw new Error(`Type de token non supporté: ${tokenType}`);
  }
  
  try {
    let body;
    let headers = { 'Content-Type': 'application/json' };
    
    // Format différent selon le type de token
    if (tokenType === TOKEN_TYPES.CLIENT) {
      body = new URLSearchParams();
      body.append('username', credentials.email);
      body.append('password', credentials.password);
      if (credentials.turnstile_token) {
        body.append('turnstile_token', credentials.turnstile_token);
      }
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
    } else {
      body = JSON.stringify(credentials);
    }
    
    const response = await fetch(`${API_BASE_URL}${config.loginEndpoint}`, {
      method: 'POST',
      headers,
      body
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
    }
    
    const data = await response.json();

    // Double authentification requise — retour anticipé sans stocker de token
    if (data.requires_2fa) {
      return { success: false, requires_2fa: true, temp_token: data.temp_token };
    }

    if (!data.access_token) {
      throw new Error('Token d\'accès non reçu du serveur');
    }

    // Stocker le token
    setToken(data.access_token, tokenType);
    
    // Stocker le refresh token si disponible
    if (data.refresh_token) {
      localStorage.setItem(config.refreshKey, data.refresh_token);
    }
    
    // Récupérer les informations utilisateur
    const authResult = await checkAuth(tokenType);
    if (!authResult.isAuthenticated) {
      throw new Error(authResult.error || 'Erreur lors de la récupération des informations utilisateur');
    }
    
    return {
      success: true,
      token: data.access_token,
      user: authResult.user
    };
    
  } catch (error) {
    console.error('Erreur de connexion:', error);
    removeToken(tokenType);
    throw error;
  }
};

/**
 * Gestionnaire d'erreurs API centralisé
 */
export const handleApiError = (error, tokenType = TOKEN_TYPES.CLIENT) => {
  console.error('Erreur API:', error);
  
  if (error.response?.status === 401) {
    removeToken(tokenType);
    
    // Rediriger vers la page de connexion appropriée
    const redirectPaths = {
      [TOKEN_TYPES.CLIENT]: '/login',
      [TOKEN_TYPES.RESTAURANT]: '/business/login',
      [TOKEN_TYPES.ADMIN]: '/admin-login'
    };
    
    const redirectPath = redirectPaths[tokenType] || '/login';
    window.location.href = redirectPath;
    
    return 'Session expirée. Veuillez vous reconnecter.';
  }
  
  return error.message || 'Une erreur est survenue';
};

/**
 * Hook pour gérer l'authentification
 */
export const useTokenAuth = (tokenType = TOKEN_TYPES.CLIENT) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const checkAuthStatus = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await checkAuth(tokenType);
        setIsAuthenticated(result.isAuthenticated);
        setUser(result.user || null);
        setError(result.error || null);
      } catch (err) {
        setIsAuthenticated(false);
        setUser(null);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthStatus();
  }, [tokenType]);
  
  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await performLogin(credentials, tokenType);
      setIsAuthenticated(true);
      setUser(result.user);
      return result;
    } catch (err) {
      setError(err.message);
      setIsAuthenticated(false);
      setUser(null);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const logout = () => {
    removeToken(tokenType);
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
  };
  
  return {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout
  };
};

export default {
  TOKEN_TYPES,
  isTokenValid,
  getToken,
  setToken,
  removeToken,
  clearAllTokens,
  checkAuth,
  performLogin,
  handleApiError,
  useTokenAuth
};
